import { useState } from 'react';

import { POST, TW_POST_TYPES } from '@/lib/posts/postTypes';
import { TYPE_IMAGE, TYPE_STATUS, TYPE_VIDEO } from '@/lib/scheduler/postType';
import { X } from '@/lib/socialProfile';

// Components
import Icon from '@/components/base/Icon';
import { CaptionPreview } from '@/components/composite/CaptionPreview';
import { Item } from '@/components/composite/MediaViewer/PostPreview/Item';

type XPost = {
    id?: string;
    caption_formatted?: string;
    tags?: { link: string }[];
    type?: string;
    sub_type?: string;
    medias?: {
        link?: string;
        text?: string;
        type?: string;
        image?: string;
        video?: string;
        thumbnail?: string;
    }[];
    data?: {
        caption?: string;
        mentions?: any[];
        medias?: any[];
    };
};

type PreviewXProps = {
    post?: XPost;
    isLoading?: boolean;
};

const ContentWrapper = ({ children }: { children: React.ReactNode }) => (
    <div
        className="group relative flex min-h-[400px] w-full flex-col rounded-md"
        aria-label="preview-x">
        {children}
    </div>
);

export const PreviewX = ({ post, isLoading, ...restProps }: PreviewXProps) => {
    const [videoPlayedId, setVideoPlayedId] = useState<number | null>(null);

    if (isLoading) {
        return (
            <span className="square block animate-pulse rounded-md bg-icoGray-300 dark:bg-icoDarkMode-deepBlack" />
        );
    }

    // Handle status posts (text only)
    if (post?.type === TYPE_STATUS) {
        return (
            <ContentWrapper>
                <CaptionPreview
                    caption={post?.data?.caption ?? post?.caption_formatted ?? ''}
                    network={X}
                    mentions={post?.data?.mentions ?? []}
                />
            </ContentWrapper>
        );
    }

    // Handle single image or video posts
    if (post?.type === TYPE_IMAGE || post?.type === TYPE_VIDEO) {
        return (
            <ContentWrapper>
                <Item
                    post={post}
                    postId={post?.id}
                    isScheduler
                    postType={POST}
                    mediaId={0}
                    media={post?.data?.medias?.[0] ?? post?.medias?.[0]}
                    config={{ withAltText: false, imgFit: 'contain' }}
                    aria-label="x-media"
                    videoPlayedId={videoPlayedId}
                    setVideoPlayedId={setVideoPlayedId}
                    remoteMedia={false}
                    repostEmail={undefined}
                    network={X}
                    {...restProps}
                />
                <CaptionPreview
                    className="mt-8"
                    caption={post?.data?.caption ?? post?.caption_formatted ?? ''}
                    network={X}
                    mentions={post?.data?.mentions ?? []}
                />
            </ContentWrapper>
        );
    }

    // Fallback for other content types or links
    return (
        <div
            className="group relative flex min-h-[400px] w-full flex-col overflow-hidden rounded-md bg-icoGray-100 p-16 dark:bg-icoDarkMode-deepBlack"
            aria-label="preview-x">
            <CaptionPreview
                aria-label="x-status"
                caption={post?.caption_formatted ?? post?.data?.caption ?? ''}
                network={X}
                mentions={post?.data?.mentions ?? []}
                className="font-GMedium text-xl"
            />

            {/* X posts can have link previews */}
            {(post?.type === TW_POST_TYPES.TYPE_LINK ||
                post?.sub_type === TW_POST_TYPES.TYPE_LINK) && (
                <a
                    href={post?.medias?.[0]?.link ?? post?.data?.medias?.[0]?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 flex min-w-150 items-center justify-between overflow-hidden rounded border border-icoGray-300 bg-white p-5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite"
                    aria-label="x-link">
                    <div className="flex flex-col space-y-1">
                        <span className="text-xxs">
                            {post?.medias?.[0]?.text ?? post?.data?.medias?.[0]?.text}
                        </span>
                        <p className="flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf">
                            <Icon icon="link" className="text-xxs" />
                            <span className="ml-2 text-xxs">
                                {post?.medias?.[0]?.link && post.medias[0].link.length > 30
                                    ? `${post.medias[0].link.substring(0, 30)}...`
                                    : post?.medias?.[0]?.link ?? post?.data?.medias?.[0]?.link}
                            </span>
                        </p>
                    </div>
                    <Icon
                        icon="chevron-right"
                        className="text-xs text-icoGray-500 dark:text-icoDarkMode-wolf"
                    />
                </a>
            )}
        </div>
    );
};

export default PreviewX;
